import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/cn";
import type { Scope } from "@/api/types";
import { scopeCountQuery } from "@/api/queries";

const BASE_OPTIONS: { value: Scope; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "managers", label: "Managers" },
  { value: "team", label: "Equipo" },
];

export interface ScopeSegmentedProps {
  value: Scope;
  onChange: (v: Scope) => void;
}

/** Tri-state segmented control. Shows live counts fetched from
 * /api/filters/scope-count when available — gives a quick hint of
 * how narrow each slice is without requiring the user to click.
 * The active gradient slides between options via a shared layoutId. */
export function ScopeSegmented({ value, onChange }: ScopeSegmentedProps) {
  const managersCount = useQuery(scopeCountQuery("managers"));
  const teamCount = useQuery(scopeCountQuery("team"));
  const reduced = useReducedMotion();

  const counts: Record<Scope, number | null> = {
    all: null,
    managers: managersCount.data?.count ?? null,
    team: teamCount.data?.count ?? null,
  };

  return (
    <div
      role="radiogroup"
      aria-label="Alcance"
      className="inline-flex gap-1 rounded-full border border-border-1 bg-white/[0.03] p-1"
    >
      {BASE_OPTIONS.map((opt) => {
        const active = opt.value === value;
        const count = counts[opt.value];
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              "relative flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors",
              active ? "text-white" : "text-text-3 hover:text-text-1",
            )}
          >
            {active && (
              <motion.span
                layoutId="active-scope"
                aria-hidden
                className="absolute inset-0 -z-0 rounded-full bg-gradient-to-br from-brand-1 to-brand-3 shadow-[0_8px_24px_-8px_rgba(236,72,153,0.5)]"
                transition={
                  reduced
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 420, damping: 32 }
                }
              />
            )}
            <span className="relative z-10">{opt.label}</span>
            {count !== null && (
              <span
                className={cn(
                  "relative z-10 rounded-full px-1.5 py-0.5 text-[10px] tabular",
                  active ? "bg-white/20 text-white" : "bg-white/5 text-text-4",
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
